import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Michael's Painting" },
      { name: "description", content: "Terms that govern your use of the Michael's Painting website, estimate tools, and customer portal." },
      { property: "og:title", content: "Terms of Service — Michael's Painting" },
      { property: "og:description", content: "Terms that govern your use of the Michael's Painting website and customer portal." },
      { name: "robots", content: "index,follow" },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  const updated = "July 2026";
  return (
    <SiteLayout>
      <section className="bg-[#030303] text-white py-24 sm:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-amber-200 text-xs font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">Terms of Service</h1>
          <p className="text-sm text-zinc-500 mb-12">Last updated: {updated}</p>

          <div className="max-w-none space-y-8 text-zinc-300 leading-relaxed">
            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Agreement</h2>
              <p>By using the Michael's Painting website, estimate tools, AI Studio, or customer portal, you agree to these terms. If you don't agree, please don't use the site.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Estimates & Proposals</h2>
              <p>Instant and AI-generated estimates are non-binding preliminary ranges based on the information you provide. A binding price is set only in a written proposal after an on-site walkthrough. Actual pricing may change based on scope, surface condition, and material selection.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">AI Studio</h2>
              <p>The AI Studio renders approximate color previews from photos you upload. Rendered images are visual approximations only — the actual painted result will vary based on lighting, substrate, and finish. Do not rely on rendered images as the sole basis for a final color decision; request physical samples before committing.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Customer Portal</h2>
              <p>The customer portal is for the individual account holder. Keep your credentials secure and notify us immediately if you suspect unauthorized access. You are responsible for activity on your account.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Warranty</h2>
              <p>Our workmanship warranty is described in your signed proposal and applies to completed projects. It does not cover damage from moisture, structural movement, ordinary wear, or work performed by others.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Payment</h2>
              <p>Deposits, milestone payments, and final balances are due per your signed proposal or invoice. Late balances may accrue interest as allowed by Wisconsin law.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Acceptable Use</h2>
              <p>Don't attempt to disrupt the site, scrape data, upload malicious content, or use the estimate or Studio tools to harass others. We may suspend accounts that abuse the service.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Content</h2>
              <p>Photos and content you upload remain yours. You grant us permission to use them to deliver the service (rendering previews, preparing proposals, project records). We won't publicly display your project photos without permission.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Disclaimer</h2>
              <p>The website is provided "as is." We work to keep it accurate and available, but we don't guarantee uninterrupted access or that all content is free of errors.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Governing Law</h2>
              <p>These terms are governed by the laws of the State of Wisconsin.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Contact</h2>
              <p>
                Questions? Reach us at{" "}
                <a href="mailto:michaelspainting0@gmail.com" className="text-amber-300 hover:text-amber-200">michaelspainting0@gmail.com</a>{" "}
                or (608) 886-4598.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}